import {Box, Paper, Typography} from "@mui/material";
import {Assessment as AnalyticsIcon, AccessTime as TimeIcon} from "@mui/icons-material";
import {useMemo} from "react";
import {targetMuscles} from "../../enums/TargetMuscles";

const SecondaryMusclesBreakdown = ({ template }) => {
    // Define muscle groupings with display names and sort order
    const muscleGroupings = {
        'LEGS_QUADS': { group: 'LEGS_QUADS', name: 'רגליים-שרשרת קדמית', order: 1 },
        'LEGS_HAMSTRINGS': { group: 'LEGS_HAMSTRINGS', name: 'רגליים-שרשרת אחורית', order: 2 },
        'LEGS_GLUTES': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
        'LEGS_ADDUCTORS': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
        'LEGS_ABDUCTORS': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
        'LEGS_CALVES': { group: 'LEGS_CALVES', name: 'רגליים-תאומים', order: 4 },
        'FEET': { group: 'FEET', name: 'כפות רגליים', order: 4.5 },
        'BACK_ROWES': { group: 'BACK', name: 'גב', order: 5 },
        'BACK_PULLS': { group: 'BACK', name: 'גב', order: 5 },
        'BACK_LOWER_BACK': { group: 'BACK', name: 'גב', order: 5 },
        'CHEST_UPPER': { group: 'CHEST', name: 'חזה', order: 6 },
        'CHEST_MIDDLE': { group: 'CHEST', name: 'חזה', order: 6 },
        'CHEST_LOWER': { group: 'CHEST', name: 'חזה', order: 6 },
        'ARMS_BICEPS': { group: 'ARMS_BICEPS', name: 'ידיים-יד קדמית', order: 7 },
        'ARMS_TRICEPS': { group: 'ARMS_TRICEPS', name: 'ידיים-יד אחורית', order: 8 },
        'ARMS_FOREARMS': { group: 'ARMS_FOREARMS', name: 'ידיים-אמות', order: 9 },
        'SHOULDERS_FRONT_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
        'SHOULDERS_LATERAL_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
        'SHOULDERS_REAR_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
        'SHOULDERS_TRAPS': { group: 'SHOULDERS_TRAPS', name: 'כתפיים-טרפזים', order: 11 },
        'NECK': { group: 'NECK', name: 'צוואר', order: 11.5 },
        'ABS_OBLIQUES': { group: 'ABS', name: 'בטן', order: 12 },
        'ABS': { group: 'ABS', name: 'בטן', order: 12 },
        'CARDIO': { group: 'CARDIO', name: 'קרדיו', order: 13 },
    };

    const calculateMuscleBreakdown = () => {
        if (!template?.workouts) return {};

        const muscleBreakdown = {};

        // Initialize all muscles with 0 sets
        targetMuscles.forEach(muscle => {
            muscleBreakdown[muscle.value] = {
                name: muscle.name,
                sets: 0,
                exercises: []
            };
        });

        // Calculate sets for each muscle
        template.workouts.forEach((workout, workoutIndex) => {
            workout.exercises?.forEach(exercise => {
                if (exercise.targetMuscle && exercise.numberOfSets) {
                    const muscle = exercise.targetMuscle;
                    if (muscleBreakdown[muscle]) {
                        muscleBreakdown[muscle].sets += exercise.numberOfSets;
                        muscleBreakdown[muscle].exercises.push({
                            name: exercise.exerciseName || `תרגיל ${exercise.exerciseId}`,
                            sets: exercise.numberOfSets,
                            workoutDay: workoutIndex + 1
                        });
                    }
                }
            });
        });

        // Group muscles according to muscleGroupings
        const groupedBreakdown = {};

        Object.entries(muscleBreakdown).forEach(([muscleKey, muscleData]) => {
            if (muscleData.sets > 0 && muscleGroupings[muscleKey]) {
                const grouping = muscleGroupings[muscleKey];
                const groupKey = grouping.group;

                if (!groupedBreakdown[groupKey]) {
                    groupedBreakdown[groupKey] = {
                        name: grouping.name,
                        sets: 0,
                        exercises: [],
                        order: grouping.order
                    };
                }

                groupedBreakdown[groupKey].sets += muscleData.sets;
                groupedBreakdown[groupKey].exercises.push(...muscleData.exercises);
            }
        });

        return groupedBreakdown;
    };

    // Get duration display text
    const getDurationDisplay = (duration) => {
        switch (duration) {
            case 'SMALL':
                return '30-45 דק׳';
            case 'MEDIUM':
                return '45-60 דק׳';
            case 'LARGE':
                return '60+ דק׳';
            default:
                return null;
        }
    };

    const muscleBreakdown = useMemo(() => calculateMuscleBreakdown(), [template?.workouts]);
    const muscleCount = Object.keys(muscleBreakdown).length;
    const durationText = getDurationDisplay(template?.trainDuration);

    const hasMuscleData = muscleCount > 0;

    return (
        <Paper
            sx={{
                backgroundColor: "#FCFCFD",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                direction: "rtl",
                mb: 1.5
            }}
            elevation={0}
        >
            {/* Compact Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 16px",
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AnalyticsIcon sx={{ color: "#64748B", fontSize: "16px" }} />
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        נפח סטים
                    </Typography>
                </Box>

                {durationText && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <TimeIcon sx={{ color: "#64748B", fontSize: "14px" }} />
                        <Typography
                            sx={{
                                fontSize: "11px",
                                fontWeight: 500,
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {durationText}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Minimized Content */}
            <Box sx={{ padding: "12px 16px" }}>
                {!hasMuscleData ? (
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#94A3B8",
                            textAlign: "center",
                            py: 1
                        }}
                    >
                        אין נתונים
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px"
                        }}
                    >
                        {/* Inline muscle chips - sorted by order */}
                        {Object.entries(muscleBreakdown)
                            .sort(([, a], [, b]) => a.order - b.order)
                            .map(([muscleKey, muscleData]) => (
                                <MuscleChip
                                    key={muscleKey}
                                    muscle={muscleData.name}
                                    sets={muscleData.sets}
                                />
                            ))}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

const MuscleChip = ({ muscle, sets }) => {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                padding: "4px 10px",
                backgroundColor: "#F1F5F9",
                borderRadius: "4px",
                border: "1px solid #E2E8F0",
                transition: "all 0.2s",
                "&:hover": {
                    backgroundColor: "#E2E8F0",
                    transform: "translateY(-1px)"
                }
            }}
        >
            <Typography
                sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#64748B",
                    lineHeight: 1.2
                }}
            >
                {muscle}
            </Typography>
            <Typography
                sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1E293B",
                    // backgroundColor: "#FFFFFF",
                    borderRadius: "3px",
                    padding: "2px 4px",
                    lineHeight: 1
                }}
            >
                {sets}
            </Typography>
        </Box>
    );
};

export default SecondaryMusclesBreakdown;